import { IAuthErrorMessage } from 'components/AuthError/AuthErrorMsg';
import { IntegrationType } from 'helpers/integration/IntegrationType';
import { IAPIContext } from 'models/context/api-context';
import { ErrorDetails } from 'models/context/error-details';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ToastContainer } from 'react-toastify';
import { AuthenticationService, ClientFactory } from 'services';
import { ErrorUtil, SSOAPIBadRequest, SSOAPIBaseError, SSOAPIServerError, SSOAPIUnexpectedResponseBody, SSOAPIUnexpectedResponseStatus } from 'services/errors';
import { Notification } from 'services/notification-service';
import Footer from '../Generics/Footer/Footer';
import Header from './Header/Header';
import LegalMessage from './LegalMessage/LegalMessage';
import SidePanel from './SidePanel/SidePanel';
import { DEFAULT_SITE_ID_NONE_VALUE } from './SiteList.renderer';
import * as S from './SiteList.styles';
import SiteRow from './SiteListing/SiteRow';

export interface ISite {
    name: string;
    id: number;
}

export interface ISiteList {
    entGuid: string;
    legalMessage: string | undefined;
    sites: ISite[] | undefined;
    token: string;
    defaultSiteId: number | undefined;
    hasDefaultSiteId: boolean;
    ssoAPIContext: IAPIContext;
    onFatalError: ({ errorCode, jsonParams, errorDetails }: IAuthErrorMessage) => void;
}

export default function SiteList({ entGuid, legalMessage, sites, token, defaultSiteId, hasDefaultSiteId, ssoAPIContext, onFatalError }: ISiteList): JSX.Element {
    const [requestInFlight, setRequestInFlight] = useState(false);
    const [sessionCreated, setSessionCreated] = useState(false);
    const [showLegalMessage, setShowLegalMessage] = useState(hasDefaultSiteId);
    const [selectedSite, setSelectedSite] = useState<ISite | null>(hasDefaultSiteId ? {
        name: 'defaultSite', id: defaultSiteId || DEFAULT_SITE_ID_NONE_VALUE
    } : null);
    const { t } = useTranslation();
    const hasLegalMessage = legalMessage !== undefined && legalMessage.length > 0;

    const authService: AuthenticationService = useMemo(() => {
        const client = ClientFactory.createClient(entGuid, IntegrationType.ETO, {});
        return new AuthenticationService(client);
    }, [entGuid]);
    const siteNotification: Notification = useMemo(
        () => new Notification(t(`notifications:${hasLegalMessage ? 'NOTIFY_SITE_AUTH' : 'NOTIFY_SITE_AUTH_LINKS_DISABLED'}`), 'site-notification', { type: 'info', isLoading: true }),
        [t, hasLegalMessage]
    );
    const nonRetryableErrorHandler = useCallback(({error_code, jsonParams, err, apiContext}: {error_code: string, jsonParams: Record<string, unknown>, err?: SSOAPIBaseError, apiContext: IAPIContext}) => {
        siteNotification.update({ render: t('notifications:NOTIFICATION_SITE_AUTH_FAILED'), type: 'error' });
        const errorDetails: ErrorDetails = { ...apiContext };
        if (err) {
            errorDetails.react_error = ErrorUtil.recurseGetRenderedPreTagErrorMessage(err);
            if (err.requestStartTimeMS) {
                errorDetails.request_start = new Date(err.requestStartTimeMS).toISOString();
            }
            if (err.requestEndTimeMS) {
                errorDetails.request_end = new Date(err.requestEndTimeMS).toISOString();
            }
            if (err.requestExecutionTimeMS) {
                errorDetails.request_duration = err.requestExecutionTimeMS.toString();
            }
        }
        onFatalError({
            errorCode: error_code,
            jsonParams,
            errorDetails
        });
    }, [onFatalError, siteNotification, t]);

    const onSelectHandler = useCallback(async (site: ISite) => {
        if (requestInFlight || sessionCreated) {
            return;
        }
        setRequestInFlight(true);
        siteNotification.show();
        try {
            const location = await authService.authenticateETOBySite(token, site, ssoAPIContext);
            setSessionCreated(true);
            setRequestInFlight(false);
            siteNotification.update({ render: t('notifications:NOTIFICATION_SITE_AUTH_SUCCESS'), type: 'success' });
            window.location.href = location.href;
        } catch (err: unknown) {
            if (err instanceof SSOAPIServerError) {
                siteNotification.update({
                    render: t('notifications:NOTIFICATION_SITE_AUTH_RETRYABLE_FAILED', {
                        error_code: err.getResponseError().error_code,
                        error_message: err.getResponseError().message
                    }),
                    type: 'error'
                });
                setRequestInFlight(false);
            } else if (err instanceof SSOAPIBadRequest) {
                nonRetryableErrorHandler({
                    error_code: err.getResponseError().error_code ?? 'BAD_REQUEST',
                    jsonParams: JSON.parse(decodeURIComponent(err.getResponseError().params || '{}')),
                    err,
                    apiContext: err.getAPIContext()
                });
            } else if (err instanceof SSOAPIUnexpectedResponseBody || err instanceof SSOAPIUnexpectedResponseStatus) {
                nonRetryableErrorHandler({
                    error_code: 'UNEXPECTED_ERROR',
                    jsonParams: { parse_error_message: err.message, cause: err.cause?.message },
                    err,
                    apiContext: err.getAPIContext()
                });
            } else {
                nonRetryableErrorHandler({
                    error_code: 'UNEXPECTED_ERROR',
                    jsonParams: { cause: (err as Error).message },
                    err: new SSOAPIBaseError('Unexpected error occured after calling SSO API', { cause: err as Error }),
                    apiContext: ssoAPIContext
                });
            }
        }
    }, [t, authService, requestInFlight, setRequestInFlight, sessionCreated, setSessionCreated, siteNotification, nonRetryableErrorHandler, token, ssoAPIContext]);

    const onSelectWithLegalMessageHandler = (site: ISite) => {
        setShowLegalMessage(true);
        setSelectedSite(site);
    };

    return (
        <S.Container>
            <ToastContainer
                hideProgressBar={false}
                newestOnTop={true}
                closeOnClick
                rtl={false}
                pauseOnFocusLoss
                pauseOnHover
            />
            <S.SidePanel className="left-side-panel">
                <S.InnerPanel>
                    <S.LeftTopDiv>
                        <Header showLegalMessage={showLegalMessage} />
                        {
                            showLegalMessage && hasLegalMessage && selectedSite ?
                                <LegalMessage legalMessage={legalMessage} onSubmit={onSelectHandler} selectedSite={selectedSite}/> :
                                <>
                                    <S.SiteRowContainer>
                                        <S.Title> Site(s): </S.Title>
                                        {sites?.map(site => <SiteRow
                                            key={site.id}
                                            site={site}
                                            disableLinks={requestInFlight || sessionCreated}
                                            onSelect={hasLegalMessage ? onSelectWithLegalMessageHandler : onSelectHandler}
                                        />)}
                                    </S.SiteRowContainer>
                                </>
                        }
                    </S.LeftTopDiv>
                    <S.LeftBottomDiv>
                        <Footer />
                    </S.LeftBottomDiv>
                </S.InnerPanel>
            </S.SidePanel>
            <S.SidePanel className="right-side-panel">
                <SidePanel />
            </S.SidePanel>
        </S.Container>
    );
}
